"use client";

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useMutation, useQuery } from "@tanstack/react-query";
import Link from "next/link";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useProfile } from "@/hooks/use-profile";

type Track = "HOMEOWNER" | "CONTRACTOR" | "STUDENT" | "OTHER";

type Presignup = {
  id: string;
  profile_id: string;
  track: Track;
  goals: string | null;
  created_at: string;
  updated_at: string;
};

type PresignupPayload = {
  track: Track;
  goals?: string;
};

async function fetchPresignup(): Promise<{ presignup: Presignup | null }> {
  const res = await fetch("/api/courses/presignup", {
    method: "GET",
    credentials: "include",
  });
  return res.json();
}

async function upsertPresignup(payload: PresignupPayload) {
  const res = await fetch("/api/courses/presignup", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data?.ok) {
    const message =
      data?.error || data?.errors?.goals?.[0] || "Unable to pre-sign up";
    throw new Error(message);
  }

  return data as { ok: true; presignup: Presignup };
}

export function CoursesWaitlistForm() {
  const { data: profileData, isLoading: profileLoading } = useProfile();
  const isLoggedIn = !!profileData?.profile;

  const returnTo = encodeURIComponent("/courses#presignup");

  const [track, setTrack] = useState<Track>("STUDENT");
  const [goals, setGoals] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const presignupQuery = useQuery({
    queryKey: ["courses-presignup"],
    queryFn: fetchPresignup,
    enabled: isLoggedIn,
    staleTime: 15_000,
  });

  const existingPresignup = presignupQuery.data?.presignup ?? null;

  const canSubmit = useMemo(() => {
    return goals.trim().length > 0;
  }, [goals]);

  const mutation = useMutation({
    mutationFn: upsertPresignup,
    onSuccess: () => {
      setSubmitted(true);
      presignupQuery.refetch();
    },
  });

  if (profileLoading) {
    return (
      <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-sm">
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className="rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-sm">
        <h3 className="font-serif text-2xl font-light tracking-tight">
          Pre-sign up for Courses
        </h3>
        <p className="mt-2 text-muted-foreground leading-relaxed">
          Create an account so we can reserve your early access and notify you
          when the first lessons drop.
        </p>
        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <Button asChild>
            <Link href={`/signup?next=${returnTo}`}>Create Account</Link>
          </Button>
          <Button variant="outline" asChild>
            <Link href={`/signin?next=${returnTo}`}>Sign In</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (existingPresignup) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        className="rounded-2xl border border-border bg-card p-8 text-center shadow-sm"
      >
        <h3 className="font-serif text-2xl font-light tracking-tight">
          You&apos;re pre-signed up
        </h3>
        <p className="mt-3 text-muted-foreground leading-relaxed">
          We&apos;ll email you when courses launch. You can update your
          preferences anytime.
        </p>
        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            variant="outline"
            onClick={() => {
              setSubmitted(false);
              setTrack(existingPresignup.track || "STUDENT");
              setGoals(existingPresignup.goals || "");
              mutation.reset();
            }}
          >
            Update Preferences
          </Button>
          <Button asChild>
            <Link href="/dashboard">Go to Dashboard</Link>
          </Button>
        </div>
      </motion.div>
    );
  }

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        className="rounded-2xl border border-border bg-card p-8 text-center shadow-sm"
      >
        <h3 className="font-serif text-2xl font-light tracking-tight">
          You&apos;re pre-signed up
        </h3>
        <p className="mt-3 text-muted-foreground leading-relaxed">
          Thanks — we&apos;ll email you as soon as the first course drops.
        </p>
        <div className="mt-6">
          <Button
            variant="outline"
            onClick={() => {
              setSubmitted(false);
              setTrack("STUDENT");
              setGoals("");
              mutation.reset();
            }}
          >
            Edit Preferences
          </Button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.form
      onSubmit={(e) => {
        e.preventDefault();
        if (!canSubmit) return;
        mutation.mutate({
          track,
          goals: goals.trim(),
        });
      }}
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-sm"
    >
      <div className="flex items-start justify-between gap-6 flex-wrap">
        <div>
          <h3 className="font-serif text-2xl font-light tracking-tight">
            Pre-sign up for Courses
          </h3>
          <p className="mt-2 text-muted-foreground leading-relaxed">
            Tell us what you want to learn so we can prioritize the first
            releases.
          </p>
        </div>
      </div>

      <div className="mt-8 grid gap-5">
        <div className="space-y-3">
          <Label>
            I&apos;m signing up as <span className="text-red-500">*</span>
          </Label>
          <RadioGroup
            value={track}
            onValueChange={(v) => setTrack(v as Track)}
            className="grid gap-3 sm:grid-cols-2"
          >
            <label className="flex items-center gap-3 rounded-lg border border-border bg-background px-4 py-3 cursor-pointer">
              <RadioGroupItem value="STUDENT" />
              <span className="text-sm font-medium">Student / DIY Learner</span>
            </label>
            <label className="flex items-center gap-3 rounded-lg border border-border bg-background px-4 py-3 cursor-pointer">
              <RadioGroupItem value="CONTRACTOR" />
              <span className="text-sm font-medium">
                Contractor / Applicator
              </span>
            </label>
            <label className="flex items-center gap-3 rounded-lg border border-border bg-background px-4 py-3 cursor-pointer">
              <RadioGroupItem value="HOMEOWNER" />
              <span className="text-sm font-medium">Homeowner</span>
            </label>
            <label className="flex items-center gap-3 rounded-lg border border-border bg-background px-4 py-3 cursor-pointer">
              <RadioGroupItem value="OTHER" />
              <span className="text-sm font-medium">Other</span>
            </label>
          </RadioGroup>
        </div>

        <div className="space-y-2">
          <Label htmlFor="waitlist-goals">
            What do you want to learn? <span className="text-red-500">*</span>
          </Label>
          <Textarea
            id="waitlist-goals"
            value={goals}
            placeholder="Example: prep and base coats, polished finish, repairs/touch-ups, estimating + client handoff..."
            onChange={(e) => setGoals(e.target.value)}
            rows={6}
            className="min-h-36"
          />
        </div>

        {mutation.isError && (
          <p className="text-sm text-red-500">{mutation.error.message}</p>
        )}

        <div className="flex items-center justify-between gap-4 flex-wrap">
          <p className="text-sm text-muted-foreground">
            <span className="text-red-500">*</span> Required fields
          </p>
          <Button type="submit" disabled={mutation.isPending || !canSubmit}>
            {mutation.isPending ? "Saving..." : "Pre-sign up"}
          </Button>
        </div>
      </div>
    </motion.form>
  );
}
